import React from 'react';
import ArrowLeft  from '@decisiv/iconix/lib/components/ArrowLeft';
import ArrowRight from '@decisiv/iconix/lib/components/ArrowRight';
import { Flex, Loading, Button } from '@decisiv/ui-components';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import PatchedTable from 'components/PatchedTable';

const LoadingState = () => (
  <Flex justifyContent="center" alignItems="center" paddingY={4}>
    <Loading size="medium" title="Loading…" />
  </Flex>
);

const EmptyState = ({ text }) => (
  <Flex justifyContent="center" alignItems="center" paddingY={4}>
    <P shade={1}>{text}</P>
  </Flex>
);

/**
 * Data table with server-side pagination, loading skeleton, and empty state.
 *
 * @param {Array}    columns       KDS Table column definitions
 * @param {Array}    data          Array of records
 * @param {boolean}  isLoading
 * @param {string}   error         Error message string (shows alert when set)
 * @param {object}   pagination    { page, per_page, total_pages, total_items }
 * @param {function} onPageChange  (newPage: number) => void
 * @param {function} getRowKey     (record) => string|number   default: r => r.id
 * @param {ReactNode} toolbar      Rendered above the table rows
 * @param {string}   emptyText
 */
const AdminTable = ({
  columns,
  data,
  isLoading,
  error,
  pagination = {},
  onPageChange,
  getRowKey = (r) => r.id,
  toolbar,
  emptyText = 'No records found.',
}) => {
  if (isLoading) return <LoadingState />;

  if (error) {
    return (
      <Flex
        border="1px solid"
        borderColor="error"
        borderRadius="4px"
        padding={2}
        marginTop={1}
      >
        <P color="error">Error loading data: {error}</P>
      </Flex>
    );
  }

  if (!data || !data.length) return <EmptyState text={emptyText} />;

  const { page, per_page, total_pages, total_items } = pagination;

  const footer = total_items != null ? () => (
    <Flex flex={1} alignItems="center" justifyContent="space-between">
      <P shade={1} style={{ margin: 0 }}>
        {(page - 1) * per_page + 1}–{Math.min(page * per_page, total_items)} of{' '}
        {total_items}
      </P>
      {total_pages > 1 && (
        <Flex alignItems="center" gap={1}>
          <Button
            icon={ArrowLeft}
            text="Previous"
            kind="secondary"
            size="small"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          />
          <P style={{ margin: '0 8px', whiteSpace: 'nowrap' }}>
            Page {page} of {total_pages}
          </P>
          <Button
            icon={ArrowRight}
            iconPosition="right"
            text="Next"
            kind="secondary"
            size="small"
            disabled={page >= total_pages}
            onClick={() => onPageChange(page + 1)}
          />
        </Flex>
      )}
    </Flex>
  ) : undefined;

  return (
    <PatchedTable
      columns={columns}
      data={data}
      getRowKey={getRowKey}
      toolbar={toolbar}
      footer={footer}
    />
  );
};

export default AdminTable;
